/**
 * Chai Dojo API client.
 *
 * Phase 1 ships a hand-written, fetch-based client. To regenerate from the
 * live API spec:
 *
 *   pnpm dev                      # boot the API
 *   pnpm gen                      # writes packages/api-client/src/generated/*
 *
 * The hand-written surface below covers the endpoints the web app needs in
 * Phase 1; once the generator runs it adds full coverage in `./generated`.
 */

import type {
  AuthConfig,
  CanvasBindingData,
  CanvasFullPayload,
  CanvasRead,
  CanvasRevisionBrief,
  Me,
  SignupRequest,
  SignupResponse,
  VariableValueResponse,
} from "@chai/shared";

export interface ApiClientConfig {
  baseUrl?: string;
  fetch?: typeof fetch;
  bearerToken?: string;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly url: string,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class ChaiApi {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private bearerToken: string | undefined;

  constructor(config: ApiClientConfig = {}) {
    this.baseUrl = (config.baseUrl ?? "/api").replace(/\/$/, "");
    this.fetchImpl =
      config.fetch ??
      (typeof fetch !== "undefined"
        ? fetch.bind(typeof window !== "undefined" ? window : globalThis)
        : () => {
            throw new Error("fetch is not defined and no fetch implementation provided");
          });
    this.bearerToken = config.bearerToken;
  }

  setBearerToken(token: string | undefined): void {
    this.bearerToken = token;
  }

  // ── Generic ─────────────────────────────────────────────────────────────

  async request<T = unknown>(path: string, init: RequestInit = {}, expectJson = true): Promise<T> {
    const url = `${this.baseUrl}${path.startsWith("/") ? path : `/${path}`}`;
    const headers = new Headers(init.headers ?? {});
    if (init.body && !headers.has("Content-Type") && !(init.body instanceof FormData)) {
      headers.set("Content-Type", "application/json");
    }
    headers.set("Accept", "application/json");
    if (this.bearerToken && !headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${this.bearerToken}`);
    }

    const response = await this.fetchImpl(url, {
      credentials: "include",
      ...init,
      headers,
    });

    if (!response.ok) {
      let body: unknown;
      try {
        body = await response.json();
      } catch {
        body = await response.text().catch(() => undefined);
      }
      const detail = (body as { detail?: string } | undefined)?.detail ?? response.statusText;
      throw new ApiError(response.status, url, detail, body);
    }

    if (response.status === 204 || !expectJson) {
      return undefined as T;
    }

    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json")) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }

  // ── Auth + me ───────────────────────────────────────────────────────────

  authConfig(): Promise<AuthConfig> {
    return this.request<AuthConfig>("/auth/config");
  }

  signup(body: SignupRequest): Promise<SignupResponse> {
    return this.request<SignupResponse>("/auth/signup", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  login(email: string, password: string): Promise<void> {
    const form = new URLSearchParams({ username: email, password });
    return this.request<void>(
      "/auth/login",
      {
        method: "POST",
        body: form,
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      },
      false,
    );
  }

  logout(): Promise<void> {
    return this.request<void>("/auth/logout", { method: "POST" }, false);
  }

  me(): Promise<Me> {
    return this.request<Me>("/me");
  }

  // ── Workspaces ───────────────────────────────────────────────────────────

  listWorkspaces() {
    return this.request<
      Array<{
        id: string;
        owner_type: "user" | "org";
        owner_id: string;
        name: string;
        slug: string;
        icon: string | null;
        settings: Record<string, unknown>;
        // The CD space id backing this workspace's access control (null only on
        // legacy rows). Used to address the workspace in /admin/spaces/{id}/*.
        space_id: string | null;
        // Work-mode directory-card fields (list endpoint only), all CD-local.
        // member_names is a capped avatar sample; members_count is the true total.
        members_count: number | null;
        canvases_count: number | null;
        artifacts_count: number | null;
        member_names: string[];
        // Demo tenants only: the demo's suggested starter questions ([] elsewhere).
        suggested_prompts: string[];
        created_by: string | null;
        created_at: string;
        updated_at: string;
      }>
    >("/workspaces");
  }

  createWorkspace(body: {
    name: string;
    org_id?: string;
    icon?: string;
    owner_type?: "user" | "org";
  }) {
    return this.request<{
      id: string;
      slug: string;
      name: string;
    }>("/workspaces", {
      method: "POST",
      body: JSON.stringify({ owner_type: "org", ...body }),
    });
  }

  // ── Canvases ─────────────────────────────────────────────────────────────

  listCanvases(workspaceId: string): Promise<CanvasRead[]> {
    return this.request<CanvasRead[]>(`/workspaces/${workspaceId}/canvases`);
  }

  createCanvas(workspaceId: string, body: { name: string; icon?: string }): Promise<CanvasRead> {
    return this.request<CanvasRead>(`/workspaces/${workspaceId}/canvases`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  getCanvas(canvasId: string): Promise<CanvasFullPayload> {
    return this.request<CanvasFullPayload>(`/canvases/${canvasId}`);
  }

  /** The canvas's version history, newest first — who wrote what, when. */
  listRevisions(canvasId: string, limit = 40) {
    return this.request<CanvasRevisionBrief[]>(`/canvases/${canvasId}/revisions?limit=${limit}`);
  }

  /**
   * Put the canvas back the way a revision found it, as a NEW revision.
   *
   * A person's action only — under the write policy, undo is what a person
   * does to what the platform made, so there is no agent-facing twin of this
   * call. Nothing is destroyed: the state being replaced is snapshotted
   * first, so a restore is itself reversible.
   */
  restoreRevision(canvasId: string, revisionId: string) {
    return this.request<CanvasFullPayload>(
      `/canvases/${canvasId}/revisions/${revisionId}/restore`,
      { method: "POST" },
    );
  }

  /**
   * Current rows for one LIVE binding (`query` / `app` / `cd`), evaluated
   * server-side as the caller (F5a).
   *
   * `vars` is the client's current variable values; the server uses only the
   * ones the binding declares in `depends_on`, so passing the whole map is
   * cheap and keeps the caller from having to know the dependency graph.
   * A binding that could not be evaluated answers 200 with `error` set.
   */
  getBindingData(
    canvasId: string,
    bindingKey: string,
    vars?: Record<string, unknown>,
  ): Promise<CanvasBindingData> {
    const query =
      vars && Object.keys(vars).length > 0
        ? `?vars=${encodeURIComponent(JSON.stringify(vars))}`
        : "";
    return this.request<CanvasBindingData>(
      `/canvases/${canvasId}/bindings/${encodeURIComponent(bindingKey)}/data${query}`,
    );
  }

  setVariableValue(
    canvasId: string,
    variableKey: string,
    value: unknown,
  ): Promise<VariableValueResponse> {
    return this.request<VariableValueResponse>(
      `/canvases/${canvasId}/variables/${variableKey}/value`,
      {
        method: "PUT",
        body: JSON.stringify({ value }),
      },
    );
  }

  // ── Threads ──────────────────────────────────────────────────────────────

  listThreads(workspaceId: string) {
    return this.request<
      Array<{
        id: string;
        workspace_id: string;
        canvas_id: string | null;
        title: string;
        last_message_at: string | null;
        archived_at: string | null;
        created_at: string;
      }>
    >(`/workspaces/${workspaceId}/threads`);
  }

  createThread(
    workspaceId: string,
    body: {
      canvas_id?: string | null;
      title?: string;
      surface?: string;
      entity_kind?: string | null;
      entity_id?: string | null;
    },
  ) {
    return this.request<{ id: string; title: string }>(`/workspaces/${workspaceId}/threads`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  /**
   * List the caller's conversations for a Plan/workspace entity, newest first —
   * backs the per-page agent rail's conversation switcher (the page-scoped
   * analogue of listCanvasThreads). Empty is normal: threads are created lazily
   * on the first send, so an empty list means the rail opens in draft mode.
   */
  listEntityThreads(
    workspaceId: string,
    params: { surface: string; entity_kind?: string | null; entity_id?: string | null },
  ) {
    const qs = new URLSearchParams({ surface: params.surface });
    if (params.entity_kind) qs.set("entity_kind", params.entity_kind);
    if (params.entity_id) qs.set("entity_id", params.entity_id);
    return this.request<
      Array<{
        id: string;
        workspace_id: string;
        canvas_id: string | null;
        title: string;
        surface: string | null;
        entity_kind: string | null;
        entity_id: string | null;
        last_message_at: string | null;
        archived_at: string | null;
        created_at: string;
      }>
    >(`/workspaces/${workspaceId}/entity-threads?${qs.toString()}`);
  }

  /**
   * Permanently delete a thread (and the cascade of its messages, agent
   * events, and any in-flight runs). The API returns 204; we surface it
   * as `void`. Callers are expected to tear down any open WebSocket
   * agent stream for the thread *before* calling this, otherwise the
   * server-side run may continue writing into a now-gone row until it
   * notices the disconnect.
   */
  deleteThread(threadId: string) {
    return this.request<void>(`/threads/${threadId}`, { method: "DELETE" });
  }

  updateThread(
    threadId: string,
    body: { title?: string; summary?: string | null; archived?: boolean },
  ) {
    return this.request<{ id: string; title: string; summary: string | null }>(
      `/threads/${threadId}`,
      {
        method: "PATCH",
        body: JSON.stringify(body),
      },
    );
  }

  /**
   * @param acknowledgedSecretWarning the person saw the composer's
   *   low-confidence "this looks like a credential" warning and chose to send
   *   anyway. It waives the warning and nothing else — a high-confidence match
   *   is withheld server-side however this is set, which is why `redactions`
   *   comes back: kinds and counts of what was withheld, never the value.
   */
  postMessage(
    threadId: string,
    content: string | Record<string, unknown>,
    acknowledgedSecretWarning = false,
  ) {
    return this.request<{
      message: { id: string; sequence: number };
      run_id: string;
      redactions: Array<{ kind: string; confidence: string; count: number }>;
    }>(`/threads/${threadId}/messages`, {
      method: "POST",
      body: JSON.stringify({
        content,
        acknowledged_secret_warning: acknowledgedSecretWarning,
      }),
    });
  }

  listMessages(threadId: string, offset = 0, limit = 200) {
    return this.request<
      Array<{
        id: string;
        thread_id: string;
        role: "user" | "assistant" | "system" | "tool";
        sequence: number;
        content: Record<string, unknown>;
        created_at: string;
      }>
    >(`/threads/${threadId}/messages?offset=${offset}&limit=${limit}`);
  }

  // ── Phase 2: Agent runs / events ────────────────────────────────────────

  startRun(threadId: string, hint?: string) {
    return this.request<{
      run_id: string;
      agent_url: string;
      bearer: string;
      ws_url: string;
    }>(`/threads/${threadId}/runs`, {
      method: "POST",
      body: JSON.stringify({ hint }),
    });
  }

  listAgentEvents(threadId: string, after = 0, limit = 200) {
    return this.request<
      Array<{
        id: string;
        thread_id: string;
        sequence: number;
        kind: string;
        event: Record<string, unknown>;
        created_at: string;
      }>
    >(`/threads/${threadId}/agent-events?after=${after}&limit=${limit}`);
  }

  // ── Phase 2: Canvas proposals ───────────────────────────────────────────

  listProposals(canvasId: string) {
    return this.request<
      Array<{
        id: string;
        canvas_id: string;
        thread_id: string;
        operations: unknown[];
        reasoning: string | null;
        status: "pending" | "approved" | "rejected";
        decided_at: string | null;
        decided_by: string | null;
        created_at: string;
        /** True when this suggestion CHANGES something rather than only
         *  adding — which, under the write policy, can only be because the
         *  person asked for that exact edit (data-loop plan §4). The UI says
         *  "you asked for this" rather than "suggested addition". */
        user_requested: boolean;
        author: {
          user_id: string;
          name: string | null;
          email: string | null;
        } | null;
        thread_title: string | null;
      }>
    >(`/canvases/${canvasId}/proposals`);
  }

  approveProposal(canvasId: string, proposalId: string) {
    return this.request<CanvasFullPayload>(
      `/canvases/${canvasId}/proposals/${proposalId}/approve`,
      { method: "POST" },
    );
  }

  rejectProposal(canvasId: string, proposalId: string) {
    return this.request<{ id: string; status: "rejected" }>(
      `/canvases/${canvasId}/proposals/${proposalId}/reject`,
      { method: "POST" },
    );
  }

  /** Roll-up: how many waiting / applied / dismissed suggestions on this canvas. */
  proposalsSummary(canvasId: string) {
    return this.request<{
      waiting: number;
      applied: number;
      dismissed: number;
      total: number;
    }>(`/canvases/${canvasId}/proposals/summary`);
  }

  /** Combined proposal + canvas snapshot for the suggestion-preview page. */
  previewProposal(canvasId: string, proposalId: string) {
    return this.request<{
      proposal: {
        id: string;
        canvas_id: string;
        thread_id: string;
        operations: unknown[];
        reasoning: string | null;
        status: "pending" | "approved" | "rejected";
        decided_at: string | null;
        decided_by: string | null;
        created_at: string;
        /** See `listProposals`. */
        user_requested: boolean;
      };
      canvas: CanvasFullPayload;
      author: {
        user_id: string;
        name: string | null;
        email: string | null;
      } | null;
    }>(`/canvases/${canvasId}/proposals/${proposalId}/preview`);
  }

  /**
   * Kick off the agent-driven canvas-creation flow.
   *
   * Persists the user's prompt as a normal message on `thread_id` and
   * enqueues a worker that drives the agent to completion. Returns 202
   * with the new message id.
   */
  bootstrapCanvas(
    canvasId: string,
    body: { thread_id: string; prompt: string; attachment_ids?: string[] },
  ) {
    return this.request<{
      enqueued: boolean;
      thread_id: string;
      message_id: string;
    }>(`/canvases/${canvasId}/bootstrap`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  /** List the calling user's conversations on this canvas — including
   * artifact-dedicated forks. The ConversationSwitcher renders the
   * forks under their parent so the user can resume an artifact
   * editor from the canvas chat list. */
  listCanvasThreads(canvasId: string) {
    return this.request<
      Array<{
        id: string;
        workspace_id: string;
        canvas_id: string | null;
        user_id: string;
        title: string;
        summary: string | null;
        archived_at: string | null;
        last_message_at: string | null;
        created_at: string;
        parent_thread_id: string | null;
        artifact_id: string | null;
      }>
    >(`/canvases/${canvasId}/threads`);
  }

  /** Promote a chat-message block (text/chart/table/...) into a one-op suggestion.
   *
   * Pass `binding` when promoting a chart or table whose data isn't on the
   * canvas yet (e.g. a `chat.share_visual` artifact). The API will prepend
   * an `add_binding` op and wire the new block to it.
   */
  proposalFromMessage(
    canvasId: string,
    body: {
      thread_id: string;
      page_key?: string;
      section_key?: string;
      block_type: string;
      config?: Record<string, unknown>;
      reasoning?: string;
      binding?: {
        binding_key: string;
        kind?: "static" | "filtered" | "computed" | "computed_pandas" | "tool_result";
        spec: Record<string, unknown>;
      };
    },
  ) {
    return this.request<{
      id: string;
      canvas_id: string;
      thread_id: string;
      operations: unknown[];
      reasoning: string | null;
      status: "pending" | "approved" | "rejected";
      decided_at: string | null;
      decided_by: string | null;
      created_at: string;
    }>(`/canvases/${canvasId}/proposals/from-message`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  // ── Phase 2: Attachments ────────────────────────────────────────────────

  presignUpload(body: {
    workspace_id: string;
    thread_id?: string;
    filename: string;
    mime: string;
    size_bytes: number;
  }) {
    return this.request<{ url: string; key: string; expires_at: string }>("/uploads/presign", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  finalizeAttachment(body: {
    key: string;
    workspace_id: string;
    thread_id?: string;
    filename: string;
    mime: string;
    size_bytes: number;
    sha256?: string;
  }) {
    return this.request<{
      id: string;
      filename: string;
      status: "uploaded" | "extracting" | "indexing" | "ready" | "failed";
    }>("/attachments/finalize", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  listAttachments(params: { workspace_id?: string; thread_id?: string }) {
    const qs = new URLSearchParams();
    if (params.workspace_id) qs.set("workspace_id", params.workspace_id);
    if (params.thread_id) qs.set("thread_id", params.thread_id);
    return this.request<
      Array<{
        id: string;
        workspace_id: string;
        thread_id: string | null;
        filename: string;
        mime: string;
        size_bytes: number;
        status: "uploaded" | "extracting" | "indexing" | "ready" | "failed";
        created_at: string;
      }>
    >(`/attachments?${qs.toString()}`);
  }

  getAttachment(attachmentId: string) {
    return this.request<{
      id: string;
      workspace_id: string;
      thread_id: string | null;
      filename: string;
      mime: string;
      size_bytes: number;
      status: "uploaded" | "extracting" | "indexing" | "ready" | "failed";
      created_at: string;
    }>(`/attachments/${attachmentId}`);
  }

  getAttachmentText(attachmentId: string, range?: string) {
    const qs = range ? `?range=${encodeURIComponent(range)}` : "";
    return this.request<{
      attachment_id: string;
      text: string;
      pages: number;
      truncated: boolean;
    }>(`/attachments/${attachmentId}/text${qs}`);
  }

  searchAttachment(attachmentId: string, q: string, top_k = 5) {
    return this.request<{
      attachment_id: string;
      query: string;
      hits: Array<{
        node_id: string;
        title: string;
        page_range: number[];
        score: number;
        excerpt: string;
      }>;
    }>(`/attachments/${attachmentId}/search?q=${encodeURIComponent(q)}&top_k=${top_k}`);
  }

  // ── Datasets (read-only for users; CRUD lives in admin app) ─────────────

  listWorkspaceDatasets(workspaceId: string) {
    return this.request<
      Array<{
        id: string;
        org_id: string;
        name: string;
        slug: string;
        description: string | null;
        icon: string | null;
        created_by: string | null;
        created_at: string;
        updated_at: string;
        file_count: number;
      }>
    >(`/workspaces/${workspaceId}/datasets`);
  }

  listDatasetFiles(workspaceId: string, datasetId: string) {
    return this.request<
      Array<{
        id: string;
        dataset_id: string;
        uploader_id: string | null;
        filename: string;
        mime: string;
        size_bytes: number;
        sha256: string | null;
        storage_key: string;
        status: "uploaded" | "extracting" | "indexing" | "ready" | "failed";
        created_at: string;
        progress?: {
          pct: number;
          label: string;
          search_ready: boolean;
          failed: boolean;
        } | null;
        search_ready?: boolean;
        error_message?: string | null;
      }>
      // Workspace-scoped so a regular member can read as themselves (the bare
      // /datasets/{id}/files route 404s and escalates to org-admin -> 403).
    >(`/workspaces/${workspaceId}/datasets/${datasetId}/files`);
  }

  getDatasetFileText(fileId: string, range?: string) {
    const qs = range ? `?range=${encodeURIComponent(range)}` : "";
    return this.request<{
      attachment_id: string;
      text: string;
      pages: number;
      truncated: boolean;
    }>(`/dataset-files/${fileId}/text${qs}`);
  }

  // ── Workflows ───────────────────────────────────────────────────────────

  listWorkflows(workspaceId: string) {
    return this.request<WorkflowSummary[]>(`/workspaces/${workspaceId}/workflows`);
  }

  getWorkflow(workflowId: string) {
    return this.request<WorkflowDetail>(`/workflows/${workflowId}`);
  }

  startWorkflowOnCanvas(canvasId: string, workflowId: string, body: { thread_id: string }) {
    return this.request<{
      run_id: string;
      phase: WorkflowRunPhase;
      pending_question: WorkflowIntakeQuestion | null;
    }>(`/canvases/${canvasId}/workflows/${workflowId}/start`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  getWorkflowRun(runId: string) {
    return this.request<WorkflowRunSnapshot>(`/workflow-runs/${runId}`);
  }

  submitWorkflowIntakeAnswer(
    runId: string,
    body: { key: string; value: unknown; skipped?: boolean },
  ) {
    return this.request<{
      run_id: string;
      phase: WorkflowRunPhase;
      pending_question: WorkflowIntakeQuestion | null;
      plan_summary: string | null;
    }>(`/workflow-runs/${runId}/intake-answer`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  confirmWorkflowPlan(runId: string) {
    return this.request<{ run_id: string; phase: WorkflowRunPhase }>(
      `/workflow-runs/${runId}/start-run`,
      { method: "POST", body: "{}" },
    );
  }

  stopWorkflowRun(runId: string) {
    return this.request<{ phase: string; requested_at?: string; already_terminal?: boolean }>(
      `/workflow-runs/${runId}/stop`,
      { method: "POST", body: "{}" },
    );
  }

  reloadWorkflowRegistry() {
    return this.request<{ count: number; workflows: WorkflowSummary[] }>(
      "/admin/workflows/reload",
      { method: "POST", body: "{}" },
    );
  }

  // ── Demo chrome (prospect demo sessions only) ────────────────────────────

  /**
   * In-app prospect feedback (the floating widget). Demo sessions only —
   * the API answers 403 for everyone else. `context.path` tells ops which
   * page the note came from.
   */
  submitDemoFeedback(body: {
    message: string;
    context?: { path?: string; workspace_id?: string };
  }) {
    return this.request<{ id: string }>("/demo/feedback", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }
}

// ── Workflow types (mirrored from chai_api.schemas.workflow) ───────────────

export type WorkflowRunPhase = "intake" | "plan" | "running" | "completed" | "stopped" | "failed";

export type WorkflowQuestionType =
  | "text"
  | "longtext"
  | "choice"
  | "multichoice"
  | "number"
  | "date"
  | "files"
  | "boolean";

export interface WorkflowIntakeOption {
  value: string;
  label: string;
  description?: string | null;
}

export interface WorkflowIntakeQuestion {
  key: string;
  label: string;
  type: WorkflowQuestionType;
  required?: boolean;
  help?: string | null;
  default?: unknown;
  options?: WorkflowIntakeOption[];
  accept?: string[];
  placeholder?: string | null;
  min?: number | null;
  max?: number | null;
}

export interface WorkflowOutputDecl {
  kind: string;
  label: string;
  /** Phase 3: locked artifact theme for planner-bypass output builds.
   * When set, the workflow runner must honour this theme verbatim via
   * `artifact.write_spec` — the LLM cannot override it. Unset means
   * the ordinary `artifact.create` planner path is used. */
  theme_id?: string | null;
  /** Optional path (relative to the workflow source dir) of a Python
   * helper that composes the full format-aware spec from canvas state
   * + intake answers. Workflow-internal — the runtime imports it lazily.
   */
  deck_template?: string | null;
}

export interface WorkflowSummary {
  id: string;
  name: string;
  description: string;
  version: string;
  icon?: string | null;
  owner?: string | null;
  tags: string[];
  visibility: string;
  source_kind: string;
  intake_question_count: number;
  outputs: WorkflowOutputDecl[];
}

export interface WorkflowDetail extends WorkflowSummary {
  intake: WorkflowIntakeQuestion[];
  plan_confirm_required: boolean;
}

export interface WorkflowRunSnapshot {
  id: string;
  workflow_id: string;
  workflow_version: string;
  workspace_id: string;
  canvas_id: string;
  thread_id: string;
  user_id: string;
  phase: WorkflowRunPhase;
  intake_state: {
    answers?: Record<string, unknown>;
    pending_key?: string | null;
    cursor?: number;
    history?: Array<{
      key: string;
      value?: unknown;
      asked_at?: string;
      answered_at?: string;
      skipped?: boolean;
    }>;
    checkpoints?: Array<{
      id: string;
      question?: WorkflowIntakeQuestion;
      asked_at?: string;
    }>;
  };
  plan_summary: string | null;
  outputs: Array<{
    kind: string;
    label: string;
    attachment_id: string;
    published_at?: string;
    status?: string;
  }>;
  started_at: string | null;
  completed_at: string | null;
  stop_requested_at: string | null;
  last_heartbeat_at: string | null;
  error_message: string | null;
  created_at: string;
}

export const createApiClient = (config?: ApiClientConfig) => new ChaiApi(config);
export type {
  AuthConfig,
  CanvasFullPayload,
  CanvasRead,
  Me,
  SignupRequest,
  SignupResponse,
  VariableValueResponse,
};
